// The ONE way the machine details panel opens (#6563 v2, item 1). Every surface that opens
// a machine (a card's name and action button, a List row's name, a Map square, a Needs
// attention row, the card menu's "Machine details", "Rename", "Instructions" and "Remove
// machine") calls `open`; nothing else
// writes `?machine=`. The url carries the machine (owner ruling G10), so a link to it opens
// it on load; WHERE the open lands (the top, Instructions, rename, the Remove confirm) is held here in
// memory, never in the url. A deep link lands at the top.
//
// Per page, never a global store: `<Fleet/>` makes one, over its own url accessor.

import { createSignal } from 'solid-js';
import type { PanelTarget } from './fleet-machine-panel';
import type { UtilBox } from './fleetUtilization';
import type { CardMenuId } from './machine-card';

export type MachinePanel = {
  /** The machine the url names, or null when the panel is shut. */
  readonly id: () => string | null;
  /** Where the current open lands. */
  readonly target: () => PanelTarget;
  /** Open the panel on a machine, landing at `target` (the top by default). */
  readonly open: (id: string, target?: PanelTarget) => void;
  /** Shut the panel: clears `?machine=`. Escape and the close button both call this. */
  readonly close: () => void;
};

export function createMachinePanel(input: {
  readonly param: () => string | null;
  readonly setParam: (id: string | null) => void;
}): MachinePanel {
  const [target, setTarget] = createSignal<PanelTarget>('top');
  return {
    id: () => {
      const id = input.param();
      return id === null || id === '' ? null : id;
    },
    target,
    open: (id, at = 'top') => {
      setTarget(at);
      input.setParam(id);
    },
    close: () => {
      setTarget('top');
      input.setParam(null);
    },
  };
}

/** What a machine's openers and its menu need: open it, open it to rename or at Remove, stop, start. */
export type MachineHandlers = {
  /** Open the machine's details panel at `target`: its top by default, or Instructions. */
  readonly onManage: (box: UtilBox, target?: PanelTarget) => void;
  /** Open the machine's details panel in rename mode (#6566). */
  readonly onRename: (box: UtilBox) => void;
  /** Open the machine's details panel at its Remove confirm. */
  readonly onRemove: (box: UtilBox) => void;
  readonly onDrain: (box: UtilBox) => void;
  readonly onUndrain: (box: UtilBox) => void;
};

/**
 * The page's three panel openers, all through `panel.open`: what every card, row, square,
 * Needs attention row and menu item is handed. `<Fleet/>` wires exactly these.
 */
export function machineOpeners(
  panel: Pick<MachinePanel, 'open'>,
): Pick<MachineHandlers, 'onManage' | 'onRename' | 'onRemove'> {
  return {
    onManage: (box, target = 'top') => panel.open(box.id, target),
    onRename: (box) => panel.open(box.id, 'rename'),
    onRemove: (box) => panel.open(box.id, 'remove'),
  };
}

/**
 * What the card menu runs beyond the panel's openers: the card's size, the Agents page, the
 * copy, "Jobs at once", which swaps the menu body for its picker (#6740), and Send work,
 * which opens the card's own form (#6770).
 */
export type CardMenuActions = MachineHandlers & {
  readonly onSendWork: (box: UtilBox) => void;
  /** Move work (#6772): the card's panel; Drain machine opens it with everything movable ticked. */
  readonly onMoveWork?: (box: UtilBox, drain: boolean) => void;
  readonly onSimplify: (simplified: boolean) => void;
  readonly onAgents: () => void;
  readonly onCopyId: (box: UtilBox) => void;
  readonly onJobsAtOnce: (box: UtilBox) => void;
  /** "Pause all jobs" / "Resume all jobs" (#6753): the card's own controls, never the drain. */
  readonly onJobsAll?: () => void;
};

/**
 * Run one card-menu item (`cardMenu`, `./machine-card`): "Machine details", "Rename",
 * "Instructions" and "Remove machine" open the panel through the page's one opener; the
 * drain and undrain are the page's own writes; "Jobs at once" opens the menu's own picker;
 * "Send work" opens the card's Send work form (#6770). A `disabled` item never reaches here.
 */
export function runCardMenuItem(id: CardMenuId, box: UtilBox, actions: CardMenuActions): void {
  switch (id) {
    case 'details':
      actions.onManage(box);
      return;
    case 'send':
      actions.onSendWork(box);
      return;
    case 'move_work':
      actions.onMoveWork?.(box, false);
      return;
    case 'drain_select':
      actions.onMoveWork?.(box, true);
      return;
    case 'instructions':
      actions.onManage(box, 'instructions');
      return;
    case 'rename':
      actions.onRename(box);
      return;
    case 'remove':
      actions.onRemove(box);
      return;
    case 'drain':
      actions.onDrain(box);
      return;
    case 'undrain':
      actions.onUndrain(box);
      return;
    case 'simplify':
      actions.onSimplify(true);
      return;
    case 'expand':
      actions.onSimplify(false);
      return;
    case 'agents':
      actions.onAgents();
      return;
    case 'copy_id':
      actions.onCopyId(box);
      return;
    case 'jobs_at_once':
      actions.onJobsAtOnce(box);
      return;
    case 'pause_jobs':
    case 'resume_jobs':
      actions.onJobsAll?.();
      return;
  }
}
